// An offer whose flight already left (PO 2026-11-03, SKY disruption b8jx84wqcf61: «la
// disrupción debería expirar todas esas ofertas, indicarlo… nunca un callejón sin salida»).
// Two shapes, both with ONE way out — a new search:
//  • `selected` — the PNR's chosen flight, sold by a live supplier (Duffel / Atlas), left with
//    no ticket: whether or not they accepted it, nobody can buy it any more;
//  • `options`  — every option rebooking holds for the PNR already left (the options view's
//    soft mark, `optionsExpired`; once the sweep ran, NO_OPTIONS `OPTIONS_DEPARTED`): nothing
//    is left to offer.
// rebooking's departed-offer sweep unwinds both on its own (a minute or two late); until it
// does — or wherever it is off — the console reads it here. A search that answered AFTER the
// options left (another no-options reason) is that search's verdict, not an expiry. Ticketed →
// never. Pure.

import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { liveCalendarProvider } from './flight-provider';

export interface IOfferExpiry {
  kind: 'selected' | 'options';
  /** The flight that left, as a label («LA0257») — `selected` only. */
  flight?: string | undefined;
  /** ISO-8601 — when it left. `selected` only. */
  departAt?: string | undefined;
  /** They accepted it (or a person did for them): Nexa was going to buy it. */
  accepted: boolean;
}

type ExpiryRow = Pick<
  DisruptionPnrGroupRow,
  | 'ticketNumber'
  | 'selectedOptionUrn'
  | 'selectedProvider'
  | 'assignedFlight'
  | 'optionsExpired'
  | 'noOptionsReason'
  | 'lastOfferResponse'
  | 'nexaPurchase'
  | 'commit'
>;

/** What the PNR's card knows better than the row: its live options read. */
export interface IExpiryView {
  /** The selected option: its supplier and legs. */
  selected?: { provider?: string | undefined; segments: ReadonlyArray<{ flightNumber?: string; departure: string }> } | undefined;
  /** Every option of the read already left. */
  optionsExpired?: boolean | undefined;
}

const at = (iso: string | undefined): number => {
  const v = Date.parse(iso ?? '');
  return Number.isFinite(v) ? v : Number.NaN;
};

export function offerExpiryOf(row: ExpiryRow, now: number = Date.now(), view: IExpiryView = {}): IOfferExpiry | null {
  if (row.ticketNumber || row.commit?.ticket) return null;
  const accepted = row.lastOfferResponse?.response === 'ACCEPTED' || !!row.nexaPurchase;
  if (row.selectedOptionUrn) {
    const leg = view.selected?.segments[0];
    const provider = view.selected?.provider ?? row.selectedProvider;
    const departAt = leg?.departure ?? row.assignedFlight?.departureAt;
    if (!liveCalendarProvider(provider) || !(at(departAt) <= now)) return null;
    return { kind: 'selected', flight: leg?.flightNumber ?? row.assignedFlight?.number, departAt, accepted };
  }
  if (row.noOptionsReason === 'OPTIONS_DEPARTED') return { kind: 'options', accepted: false };
  if (row.noOptionsReason) return null;
  if (view.optionsExpired ?? row.optionsExpired) return { kind: 'options', accepted: false };
  return null;
}
